import { Injectable } from '@angular/core';
import { where } from '@angular/fire/firestore';
import { CollectionPaths } from '@models/collections';
import {
	BusinessMatterClientWithAssociations,
	PersonMatterClientWithAssociations,
} from '@models/matter';
import {
	PortalInvite,
	PortalInvitee,
	PortalInviteStatus,
} from '@models/portal-invites';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { GlobalStateModel } from '@state/FirestoreStateBaseForGlobal';
import { DEFAULT_STATE_BASE } from '@state/FirestoreStateBase';
import { createInviteesFromClients } from '@globalUtils/client-intake';
import { MatterClients } from '@state/global/matter_clients';
import * as actions from './portal-invite.actions';
import { FirestoreStateBaseForBusinessWithSubscription } from '../FirestoreStateBaseForBusiness';

interface PortalInviteStateModel extends GlobalStateModel {
	data: PortalInvite[];
}

type Ctx = StateContext<PortalInviteStateModel>;

const DEFAULT_STATE: PortalInviteStateModel = {
	...DEFAULT_STATE_BASE,
	data: [],
};

@State<PortalInviteStateModel>({
	name: 'portalInvite',
	defaults: DEFAULT_STATE,
})
@Injectable()
export class PortalInviteState extends FirestoreStateBaseForBusinessWithSubscription<
	PortalInvite,
	PortalInviteStateModel
> {
	constructor() {
		super(
			() => CollectionPaths.portalInvites(),
			(doc) => ({ data: doc as PortalInvite[] }),
			() => DEFAULT_STATE,
			() => {
				return [where('businessId', '==', this.businessId)];
			},
		);
	}

	@Selector([PortalInviteState])
	static getAll(state: PortalInviteStateModel): PortalInvite[] {
		return state?.data ?? [];
	}

	@Selector([PortalInviteState.getAll])
	static getInvite(
		items: PortalInvite[],
	): (id: string) => PortalInvite | undefined {
		return (id: string): PortalInvite | undefined =>
			items?.find((invite) => invite.id === id);
	}

	@Selector([PortalInviteState.getAll])
	static getInvitesByMatter(
		items: PortalInvite[],
	): (matterId: string) => PortalInvite[] {
		return (matterId: string): PortalInvite[] =>
			items?.filter((invite) => invite.matterId === matterId) ?? [];
	}

	@Selector([PortalInviteState.getAll])
	static getInvitesByBusiness(
		items: PortalInvite[],
	): (businessId: string) => PortalInvite[] {
		return (businessId: string): PortalInvite[] =>
			items?.filter((invite) => invite.businessId === businessId) ?? [];
	}

	@Selector([PortalInviteState.getAll])
	static getInviteByPortalServiceId(
		items: PortalInvite[],
	): (portalServiceId: string) => PortalInvite | undefined {
		return (portalServiceId: string): PortalInvite | undefined =>
			items?.find(
				(invite) =>
					invite.portalServiceId === portalServiceId &&
					invite.status !== PortalInviteStatus.CANCELLED,
			);
	}

	@Selector([
		MatterClients.get_personMatterClientsWithAssociationMapped,
		MatterClients.get_matterClientsWithAssociationMapped,
	])
	static createInviteesFromMatterClients(
		matterClients: PersonMatterClientWithAssociations[],
		associationClients: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
	): PortalInvitee[] {
		return createInviteesFromClients(matterClients, associationClients);
	}

	@Action(actions.CreatePortalInvite)
	async createPortalInvite(
		ctx: Ctx,
		{ payload }: actions.CreatePortalInvite,
	): Promise<void> {
		await this.repo.CreateOrMerge(
			payload.portalInvite.id,
			payload.portalInvite,
		);
	}

	@Action(actions.AddInvitees)
	async addInvitees(ctx: Ctx, { payload }: actions.AddInvitees): Promise<void> {
		await this.repo.AddToArray(payload.inviteId, 'invitees', payload.invitees);
		await this.repo.CreateOrMerge(payload.inviteId, {
			updatedAt: Date.now(),
		});
	}

	@Action(actions.RemoveInvitees)
	async removeInvitees(
		ctx: Ctx,
		{ payload }: actions.RemoveInvitees,
	): Promise<void> {
		await this.repo.RemoveFromArray(
			payload.inviteId,
			'invitees',
			payload.invitees,
		);
		await this.repo.CreateOrMerge(payload.inviteId, {
			updatedAt: Date.now(),
		});
	}

	@Action(actions.UpdateInviteeEmail)
	async updateInviteeEmail(
		ctx: Ctx,
		{ payload }: actions.UpdateInviteeEmail,
	): Promise<void> {
		const state = ctx.getState();

		const invitees = state.data.find(
			(inv) => inv.id === payload.inviteId,
		)?.invitees;

		if (!invitees || invitees?.length === 0) return;

		const updatedInvitees = invitees.map((invitee) => {
			if (invitee.clientId === payload.clientId) {
				return {
					...invitee,
					email: payload.newEmail,
				};
			}

			return invitee;
		});

		await this.repo.CreateOrMerge(payload.inviteId, {
			invitees: updatedInvitees,
			updatedAt: Date.now(),
		});
	}

	@Action(actions.UpdateStatus)
	async updateStatus(
		ctx: Ctx,
		{ payload }: actions.UpdateStatus,
	): Promise<void> {
		await this.repo.CreateOrMerge(payload.inviteId, {
			status: payload.status,
			updatedAt: Date.now(),
			updatedBy: payload.updatedBy,
		});

		await this.repo.AddToArray(payload.inviteId, 'statusHistory', [
			{
				status: payload.status,
				updatedBy: payload.updatedBy,
				updatedAt: Date.now(),
			},
		]);
	}

	@Action(actions.UpdatePortalInvite)
	async updatePortalInvite(
		ctx: Ctx,
		{ payload }: actions.UpdatePortalInvite,
	): Promise<void> {
		const { inviteId, portalInvite, updatedBy } = payload;
		if (portalInvite.id && portalInvite.id !== inviteId) {
			throw new Error('Cannot change the ID of a portal invite');
		}

		await this.repo.CreateOrMerge(payload.inviteId, {
			...portalInvite,
			updatedBy: updatedBy,
			updatedAt: Date.now(),
		});
	}

	@Action(actions.DeactivatePortalInvite)
	async deactivatePortalInvite(
		ctx: Ctx,
		{ payload }: actions.DeactivatePortalInvite,
	): Promise<void> {
		await this.repo.CreateOrMerge(payload.inviteId, {
			status: PortalInviteStatus.CANCELLED,
			updatedAt: Date.now(),
			updatedBy: payload.updatedBy,
		});

		await this.repo.AddToArray(payload.inviteId, 'statusHistory', [
			{
				status: PortalInviteStatus.CANCELLED,
				updatedBy: payload.updatedBy,
				updatedAt: Date.now(),
			},
		]);
	}
}
